import React, {useState} from 'react';
import TextField, {TextFieldProps} from '@mui/material/TextField';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import clsx from 'clsx';
import './TextFieldComponent.scss';
import {FilledInputProps, Typography} from '@mui/material';
import {formatCardNumber} from '../../../../../utils/formatters';
import {useFieldFocusStyle} from '../../../../../hooks/utils/useFieldFocusStyle';
import Cancel from '@mui/icons-material/Cancel';
import Search from '@mui/icons-material/Search';
import CreateJobsFieldsWrapper from '../case-context-container/CreateJobsFieldsWrapper';

type StateType = 'enabled' | 'error' | 'inactive';
type SupportedTypes = 'text' | 'password' | 'card' | 'search' | 'clear';
type SizeType = 'large' | 'medium' | 'small';

export interface TextFieldComponentProps
  extends Omit<TextFieldProps, 'variant' | 'type'> {
  state?: StateType;
  supportText?: string;
  sizeVariant?: SizeType;
  showErrorIcon?: boolean;
  type?: SupportedTypes;
  headerLabel?: string;
  isRequired?: boolean;
  slotProps?: TextFieldProps['slotProps']; // allow parent to pass slotProps
  onClear?: () => void; // clear handler for search type
  headerAriaLabel?: string; // aria-label for the header element
  tabIndex?: number; // tabIndex for the header element
}

const TextFieldComponent: React.FC<TextFieldComponentProps> = ({
  state = 'enabled',
  supportText,
  headerLabel,
  isRequired,
  sizeVariant = 'large',
  showErrorIcon = false,
  placeholder,
  className,
  type = 'text',
  slotProps, // accept here
  onClear,
  headerAriaLabel,
  tabIndex,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isError = state === 'error';
  const isInactive = state === 'inactive';

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: 'tabFocusedBorder',
    mouseFocusClass: 'mouseFocusedBorder',
  });

  const onFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    focusHandler?.(e);
    rest.onFocus?.(e);
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseKeyDownHandler?.(e);
    rest.onMouseDown?.(e);
  };

  // password visibility toggle handler
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Custom onChange handler (with card formatting support & max length)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'card') {
      // keep only digits
      let digitsOnly = e.target.value.replace(/\D/g, '');

      // restrict to max 16 digits
      if (digitsOnly.length > 16) {
        digitsOnly = digitsOnly.slice(0, 16);
      }

      // apply formatting
      const formattedValue = formatCardNumber(digitsOnly);

      // set back the formatted value
      e.target.value = formattedValue;
    }

    rest.onChange?.(e); // forward event to parent
  };

  const helperTextId = supportText ? `${rest.id}-helper-text` : undefined;
  const errorTextId = isError ? `${rest.id}-error-text` : undefined;
  const triggerClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    if (!rest.onChange) return;

    const event = {
      target: {
        value: '',
        name: rest.name,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    rest.onChange(event);
  };

  const hasValue = Boolean(rest?.value);
  const showSearchType = type === 'search' && !isError && !showErrorIcon;
  const showClearButton =
    (type === 'search' && hasValue && !isError && !showErrorIcon) ||
    (type === 'clear' && hasValue);

  return (
    <CreateJobsFieldsWrapper
      MIN_HEIGHT={170}
      header={
        <Typography
          className="textfield-label"
          data-testid="textfield-label"
          tabIndex={tabIndex}
        >
          {headerLabel}
          {!isRequired ? (
            <span
              className="optional-text"
              data-testid={`text-field-component-header-lable-${headerLabel?.replaceAll(' ', '-')}`}
            >
              {' (optional)'}
            </span>
          ) : (
            <span
              className="required-text"
              data-testid={`text-field-component-header-lable-required-${headerLabel?.replaceAll(' ', '-')}`}
              aria-label="is required"
            >
              {'*'}
            </span>
          )}
        </Typography>
      }
      className={clsx('textfield-wrapper', className)}
      ariaLabel={headerAriaLabel}
    >
      <TextField
        {...rest}
        type={type === 'password' && !showPassword ? 'password' : 'text'}
        variant="filled"
        data-testid="text-field-input"
        placeholder={placeholder}
        disabled={isInactive}
        error={isError}
        aria-invalid={isError}
        aria-disabled={isInactive}
        aria-describedby={helperTextId}
        aria-errormessage={errorTextId}
        onChange={handleChange}
        label={rest.label}
        slotProps={{
          ...slotProps, // merge with parent props
          input: {
            disableUnderline: true,
            ...slotProps?.input,
            endAdornment: (
              <>
                {/* parent provided adornment */}
                {(slotProps?.input as Partial<FilledInputProps>)?.endAdornment}

                {/* Error Icon (decorative) */}
                {isError && showErrorIcon && (
                  <ErrorOutlineIcon
                    aria-hidden="true"
                    className="textfield-error-icon"
                  />
                )}

                {/* Password Toggle Icon */}
                {type === 'password' && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      data-testid="toggle-password"
                      edge="end"
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showPassword ? (
                        <VisibilityOff
                          aria-hidden="true"
                          className="textfield-icon-color"
                        />
                      ) : (
                        <Visibility
                          aria-hidden="true"
                          className="textfield-icon-color"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                )}

                {/* Payment Card Icon (decorative) */}
                {type === 'card' && (
                  <InputAdornment position="end">
                    <PaymentIcon
                      aria-hidden="true"
                      className="textfield-icon-color"
                    />
                  </InputAdornment>
                )}

                {(showSearchType || showClearButton) && (
                  <InputAdornment position="end">
                    {showClearButton ? (
                      <IconButton
                        onClick={triggerClear}
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            triggerClear();
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        className="clear-icon-btn"
                        data-testid="clear-button"
                      >
                        <Cancel
                          className="clear-btn"
                          fontSize={
                            sizeVariant === 'large' ? 'medium' : 'small'
                          }
                        />
                      </IconButton>
                    ) : (
                      <Search
                        aria-hidden="true"
                        data-testid="search-icon"
                        className="textfield-icon-color"
                        fontSize={sizeVariant === 'large' ? 'medium' : 'small'}
                      />
                    )}
                  </InputAdornment>
                )}
              </>
            ),
          } as Partial<FilledInputProps>,
          formHelperText: {
            id: helperTextId,
            className: 'textfield-helper',
            ...slotProps?.formHelperText,
          },
        }}
        className={clsx(
          'textfield',
          {
            'textfield--error': isError,
            'textfield--inactive': isInactive,
            'textfield--enabled': state === 'enabled',
            'textfield--show-password': type === 'password' && showPassword,
          },
          `textfield--${sizeVariant}`,
          borderClass
        )}
        helperText={
          isError ? (
            <span
              id={errorTextId}
              role="alert"
              data-testid={`text-field-component-error-${helperTextId}`}
            >
              {supportText}
            </span>
          ) : (
            supportText
          )
        }
        onFocus={onFocus}
        onMouseDown={onMouseDown}
      />
    </CreateJobsFieldsWrapper>
  );
};

export default TextFieldComponent;
